<template>
    <v-navigation-drawer v-model="navOpen" temporary>
        <v-list density="compact" nav>
            <v-list-item to="/" prepend-icon="mdi-home" title="App index" exact />
            <v-divider class="my-2" />
            <v-list-item
                v-for="entry in visibleEntries"
                :key="entry.id"
                :to="entry.routePath"
                :prepend-icon="entry.icon"
                :title="entry.title"
            />
        </v-list>
    </v-navigation-drawer>

    <v-app-bar color="white" border="b" flat>
        <v-app-bar-nav-icon @click="navOpen = !navOpen" />
        <v-app-bar-title>
            <span class="text-body-1 font-weight-medium">
                {{ routeTitle }}
                <span v-if="session.tenantId.value" class="text-medium-emphasis">
                    — {{ session.tenantLabel.value }}
                </span>
            </span>
        </v-app-bar-title>

        <template #append>
            <!--
              Teleport target. The bar belongs to the shell, but an app may own
              controls that live in it — sale-intake puts its dataType chip and
              settings toggle here. Apps render into this with
              <Teleport to="#app-bar-extra" defer>.
            -->
            <div id="app-bar-extra" class="d-flex align-center" />

            <v-menu>
                <template #activator="{ props: menuProps }">
                    <v-btn icon v-bind="menuProps">
                        <v-avatar size="32" color="primary">
                            <v-img v-if="picture" :src="picture" />
                            <span v-else class="text-caption text-white">{{ initial }}</span>
                        </v-avatar>
                    </v-btn>
                </template>
                <v-list density="compact">
                    <v-list-item :title="name" :subtitle="email" />
                    <v-divider />
                    <v-list-item
                        title="Sign out"
                        prepend-icon="mdi-logout"
                        data-testid="sign-out"
                        @click="session.signOut()"
                    />
                </v-list>
            </v-menu>
        </template>
    </v-app-bar>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { entriesForTenant, navEntries } from './registry';
import { useSession } from './session/session';

const session = useSession();
const route = useRoute();
const navOpen = ref(false);

// Apps gated to another tenant are not listed. The route guard enforces the same
// rule for anyone who arrives by URL rather than by clicking.
const visibleEntries = computed(() => entriesForTenant(navEntries, session.tenantId.value));

const routeTitle = computed(() => (route.meta.title as string | undefined) ?? 'UAT Apps');

const picture = computed(() => session.user.value?.picture as string | undefined);
const name = computed(() => (session.user.value?.name as string | undefined) ?? 'User');
const email = computed(() => session.user.value?.email as string | undefined);
const initial = computed(() => name.value.charAt(0).toUpperCase() || '?');
</script>
